import { type FC, useEffect, useRef } from 'react';
// Import Configs
import { AlertHolderSelector, AlertCloseButtonSelector } from './config';

interface Props {
    message: string;
    duration: number;
}

/**
 * Toaster body that owns the auto-close timer so the toaster stays open while the mouse is over it.
 *
 * The geneui Toaster timer cannot be paused from outside, so the toaster is shown with a duration that never
 * fires and this component closes it by clicking the Alert close icon when its own timer elapses.
 * Hovering the alert stops the timer; leaving it restarts the full duration.
 */
const ToasterMessage: FC<Props> = ({ message, duration }) => {
    const containerRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const alertHolder = containerRef.current?.closest<HTMLElement>(AlertHolderSelector);

        if (!alertHolder) {
            return;
        }

        let timerId: number | undefined;

        const closeToaster = () => alertHolder.querySelector<HTMLElement>(AlertCloseButtonSelector)?.click();
        const startTimer = () => {
            timerId = window.setTimeout(closeToaster, duration);
        };
        const stopTimer = () => window.clearTimeout(timerId);

        startTimer();
        alertHolder.addEventListener('mouseenter', stopTimer);
        alertHolder.addEventListener('mouseleave', startTimer);

        return () => {
            stopTimer();
            alertHolder.removeEventListener('mouseenter', stopTimer);
            alertHolder.removeEventListener('mouseleave', startTimer);
        };
    }, [duration]);

    return <span ref={containerRef}>{message}</span>;
};

export default ToasterMessage;
